// v10/src/components/CompletionScreen.js
// AUDIT: soft-delete + server timestamp + offline queue + PDF obligatorio offline
// FASE 5: Flujo de autorización de vuelo post-chequeo
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AlertTriangle, CheckCircle2,
  Loader, Upload, Save, AlertCircle,
  FileDown, RefreshCw, PlusCircle, WifiOff,
  Clock, Shield, Plane, SkipForward,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { useInspection } from '../hooks/useInspection';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import { POLLING_INTERVALS } from '../config/realtime';
import ConfirmModal from './common/ConfirmModal';
import AogOverrideModal from './admin/lifespan/AogOverrideModal';
import { useToast } from '../hooks/useToast';
import { PHASE_APPROVED_TITLES, PHASE_ORDER } from '../utils/checklist';

// Sub-etapa 3.2c-iii — títulos de sección para el PDF consolidado (distintos
// de PHASE_APPROVED_TITLES, que son el título de éxito en pantalla).
const PHASE_SECTION_TITLES = {
  prevuelo:         'PREVUELO',
  puesta_en_marcha: 'PUESTA EN MARCHA',
  prueba_motor:     'PRUEBA DE MOTOR',
};

const formatTime = (date) => {
  if (!date) date = new Date();
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

// ─── Warning sparks ───────────────────────────────────────────────
const SPARK_COLORS = ['#f59e0b','#ef4444','#f97316','#fbbf24','#dc2626'];
const WarningSparks = () => {
  const sparks = Array.from({ length: 28 }, (_, i) => ({
    id: i, x: Math.random() * 100, delay: Math.random() * 2,
    duration: 1.8 + Math.random() * 1.4,
    color: SPARK_COLORS[i % SPARK_COLORS.length],
    size: 4 + Math.random() * 6, rotate: Math.random() * 360,
  }));
  return (
    <div style={{ position:'fixed', inset:0, pointerEvents:'none', overflow:'hidden', zIndex:0 }}>
      {sparks.map(p => (
        <motion.div key={p.id}
          initial={{ y:'110vh', x:`${p.x}vw`, opacity:0, rotate:0 }}
          animate={{ y:'-10vh', opacity:[0,0.7,0.7,0], rotate:p.rotate }}
          transition={{ duration:p.duration, delay:p.delay, ease:'easeOut', repeat:Infinity, repeatDelay:Math.random()*3+1 }}
          style={{ position:'absolute', bottom:0, width:p.size, height:p.size, borderRadius:'50%', background:p.color, filter:'blur(1px)' }}
        />
      ))}
    </div>
  );
};

// ─── Confetti ─────────────────────────────────────────────────────
const CONFETTI_COLORS = ['#22c55e','#3b82f6','#8b5cf6','#f59e0b','#ec4899','#06b6d4','#f97316'];
const Confetti = () => {
  const particles = Array.from({ length: 52 }, (_, i) => ({
    id: i, x: Math.random() * 100, delay: Math.random() * 1.5,
    duration: 2.4 + Math.random() * 2,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 5 + Math.random() * 9, rotate: Math.random() * 720,
    isCircle: i % 3 === 0,
  }));
  return (
    <div style={{ position:'fixed', inset:0, pointerEvents:'none', overflow:'hidden', zIndex:0 }}>
      {particles.map(p => (
        <motion.div key={p.id}
          initial={{ y:-20, x:`${p.x}vw`, opacity:1, rotate:0 }}
          animate={{ y:'110vh', opacity:[1,1,0.5,0], rotate:p.rotate }}
          transition={{ duration:p.duration, delay:p.delay, ease:'easeIn', repeat:Infinity, repeatDelay:Math.random()*4+1 }}
          style={{ position:'absolute', top:0, width:p.size, height:p.isCircle ? p.size : p.size*0.4, borderRadius:p.isCircle ? '50%' : '2px', background:p.color }}
        />
      ))}
    </div>
  );
};

// ─── Stat chip ────────────────────────────────────────────────────
const StatChip = ({ value, label, color, delay, darkMode }) => (
  <motion.div
    initial={{ scale:0, opacity:0 }} animate={{ scale:1, opacity:1 }}
    transition={{ delay, type:'spring', stiffness:220, damping:16 }}
    style={{ flex:1, padding:'14px 6px', borderRadius:'16px', textAlign:'center', background: darkMode ? `${color}12` : `${color}0e`, border:`1.5px solid ${color}28` }}
  >
    <div style={{ fontSize:'24px', fontWeight:900, color, lineHeight:1, marginBottom:'4px' }}>{value}</div>
    <div style={{ fontSize:'10px', fontWeight:700, color: darkMode ? 'rgba(255,255,255,0.38)' : 'rgba(15,30,60,0.45)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{label}</div>
  </motion.div>
);

// ═════════════════════════════════════════════════════════════════
// PANTALLA DE CLASIFICACIÓN DE ANOMALÍAS
// ═════════════════════════════════════════════════════════════════
const AnomalyClassificationScreen = ({ observationItems, sessionData, inspectionId, darkMode, pilotName, onComplete }) => {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [decisions, setDecisions] = useState(
    observationItems.map(obs => ({ ...obs, pilot_decision: null, photos: obs.photos || [] }))
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');
  // A6.2 parte 2B — payload del 409 AOG cuando el backend bloquea la creación.
  const [aogBlock, setAogBlock]     = useState(null);

  const allDecided   = decisions.every(d => d.pilot_decision !== null);
  const needsReview  = decisions.filter(d => d.pilot_decision === 'needs_review');
  const canFly       = decisions.filter(d => d.pilot_decision === 'can_fly');

  const setDecision = (idx, decision) => {
    setDecisions(prev => prev.map((d, i) => i === idx ? { ...d, pilot_decision: decision } : d));
  };

  const uploadObservationPhotos = async (authResult) => {
    try {
      const squawkIds  = authResult?.data?.squawk_ids || authResult?.squawk_ids || [];
      const obsWithPhotos = decisions.filter(d => d.photos && d.photos.length > 0);
      if (squawkIds.length > 0 && obsWithPhotos.length > 0) {
        await Promise.allSettled(
          squawkIds.flatMap((squawkId, i) => {
            const obs = obsWithPhotos[i];
            if (!obs?.photos?.length) return [];
            return obs.photos.map(p =>
              api.post('/api/images/squawk/upload', {
                squawkId,
                image:     p.data,
                mediaType: p.mediaType || 'image/jpeg',
              })
            );
          })
        );
      }
    } catch (_) {}
  };

  const postFlightAuth = async (extra = {}) => {
    return api.post('/api/flight-auth', {
      inspection_id: inspectionId,
      aircraft_id:   sessionData.aircraft?.id,
      observations:  decisions.map(d => ({
        text:            d.text,
        comment:         d.comment || null,
        pilot_decision:  d.pilot_decision,
      })),
      ...extra,
    });
  };

  const handleSubmit = async () => {
    if (!allDecided) return;
    setSubmitting(true); setError('');
    try {
      const res = await postFlightAuth();
      await uploadObservationPhotos(res.data);
      onComplete(res.data);
    } catch (err) {
      // A6.2 parte 2B — 409 AIRCRAFT_AOG: abrir modal de override en vez del toast genérico.
      // 503 AOG_CHECK_UNAVAILABLE (fail-closed por timeout/error del check): mismo modal,
      // copy adaptado para que el piloto pueda reintentar o forzar con justificación.
      const code = err.response?.data?.error_code;
      if (
        (err.response?.status === 409 && code === 'AIRCRAFT_AOG') ||
        (err.response?.status === 503 && code === 'AOG_CHECK_UNAVAILABLE')
      ) {
        setAogBlock({ ...err.response.data, unavailable: code === 'AOG_CHECK_UNAVAILABLE' });
        setSubmitting(false);
        return;
      }
      setError(err.response?.data?.message || 'Error al enviar la solicitud');
    } finally { setSubmitting(false); }
  };

  // Confirmación del override desde el modal — reintenta el POST con
  // override_aog + override_reason. Si el segundo intento falla, mostramos
  // toast y dejamos el modal abierto para que el usuario decida.
  const handleAogOverride = async (overrideReason) => {
    setSubmitting(true);
    try {
      const res = await postFlightAuth({
        override_aog: true,
        override_reason: overrideReason,
      });
      await uploadObservationPhotos(res.data);
      setAogBlock(null);
      toast.warning(t('flight_auth_created_with_aog_override'));
      onComplete(res.data);
    } catch (err) {
      const msg = err.response?.data?.message || t('aog_override_retry_error');
      toast.error(msg);
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background: darkMode ? 'linear-gradient(160deg,#130a00,#1a0c0c)' : 'linear-gradient(160deg,#fffbeb,#fff7ed)', position:'relative', overflow:'hidden' }}>
      <WarningSparks />
      <motion.div
        initial={{ scale:0.9, opacity:0, y:30 }} animate={{ scale:1, opacity:1, y:0 }}
        transition={{ type:'spring', stiffness:110, damping:18 }}
        style={{ position:'relative', zIndex:2, maxWidth:'520px', width:'100%', background: darkMode?'rgba(18,8,4,0.92)':'rgba(255,255,255,0.92)', backdropFilter:'blur(24px)', borderRadius:'32px', padding:'32px', boxShadow: darkMode?'0 24px 80px rgba(0,0,0,0.6)':'0 24px 80px rgba(245,158,11,0.15)' }}>

        <div style={{ textAlign:'center', marginBottom:'24px' }}>
          <motion.div
            animate={{ scale:[1,1.08,1] }} transition={{ duration:2, repeat:Infinity }}
            style={{ width:'72px', height:'72px', borderRadius:'50%', background:'linear-gradient(135deg,#f59e0b,#ef4444)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', boxShadow:'0 10px 36px rgba(245,158,11,0.4)' }}>
            <AlertTriangle style={{ width:'36px', height:'36px', color:'white' }} />
          </motion.div>
          <h2 style={{ margin:'0 0 8px', fontSize:'22px', fontWeight:900, color: darkMode?'white':'#0f172a' }}>
            Anomalías registradas
          </h2>
          <p style={{ margin:0, fontSize:'14px', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', lineHeight:1.5 }}>
            Durante el chequeo registraste {observationItems.length} anomalía{observationItems.length > 1 ? 's' : ''}.<br/>
            Para cada una, ¿el avión puede volar así?
          </p>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:'12px', marginBottom:'24px' }}>
          {decisions.map((obs, idx) => (
            <motion.div key={idx}
              initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} transition={{ delay:idx * 0.08 }}
              style={{ borderRadius:'16px', border: obs.pilot_decision === 'needs_review' ? '1.5px solid rgba(239,68,68,0.4)' : obs.pilot_decision === 'can_fly' ? '1.5px solid rgba(22,163,74,0.4)' : (darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0'), background: obs.pilot_decision === 'needs_review' ? 'rgba(239,68,68,0.06)' : obs.pilot_decision === 'can_fly' ? 'rgba(22,163,74,0.06)' : (darkMode?'rgba(255,255,255,0.04)':'#f8fafc'), padding:'14px 16px', transition:'all 0.2s' }}>
              <p style={{ margin:'0 0 12px', fontSize:'14px', fontWeight:600, color: darkMode?'rgba(255,255,255,0.85)':'#0f172a', lineHeight:1.4 }}>
                {obs.text}
              </p>
              {obs.comment && (
                <p style={{ margin:'0 0 12px', fontSize:'12px', color: darkMode?'rgba(255,255,255,0.4)':'#64748b', fontStyle:'italic' }}>
                  "{obs.comment}"
                </p>
              )}
              <div style={{ display:'flex', gap:'8px' }}>
                <motion.button
                  onClick={() => setDecision(idx, 'can_fly')}
                  whileTap={{ scale:0.97 }}
                  style={{ flex:1, padding:'10px', borderRadius:'12px', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:700, fontSize:'13px', background: obs.pilot_decision === 'can_fly' ? '#16a34a' : (darkMode?'rgba(22,163,74,0.1)':'rgba(22,163,74,0.08)'), color: obs.pilot_decision === 'can_fly' ? 'white' : '#16a34a', transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                  <CheckCircle2 style={{ width:'14px', height:'14px' }} />
                  Puede volar
                </motion.button>
                <motion.button
                  onClick={() => setDecision(idx, 'needs_review')}
                  whileTap={{ scale:0.97 }}
                  style={{ flex:1, padding:'10px', borderRadius:'12px', border:'none', cursor:'pointer', fontFamily:'inherit', fontWeight:700, fontSize:'13px', background: obs.pilot_decision === 'needs_review' ? '#ef4444' : (darkMode?'rgba(239,68,68,0.1)':'rgba(239,68,68,0.08)'), color: obs.pilot_decision === 'needs_review' ? 'white' : '#ef4444', transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                  <AlertTriangle style={{ width:'14px', height:'14px' }} />
                  Necesita revisión
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {allDecided && (
          <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
            style={{ padding:'12px 16px', borderRadius:'14px', marginBottom:'16px', background: needsReview.length > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(22,163,74,0.08)', border: needsReview.length > 0 ? '1px solid rgba(239,68,68,0.2)' : '1px solid rgba(22,163,74,0.2)' }}>
            {needsReview.length === 0 ? (
              <p style={{ margin:0, fontSize:'13px', fontWeight:600, color:'#16a34a' }}>
                ✅ Todas las anomalías son menores — podés volar
              </p>
            ) : (
              <p style={{ margin:0, fontSize:'13px', fontWeight:600, color:'#ef4444' }}>
                ⚠️ {needsReview.length} anomalía{needsReview.length > 1 ? 's' : ''} requiere{needsReview.length > 1 ? 'n' : ''} revisión — se notificará al instructor
              </p>
            )}
          </motion.div>
        )}

        {error && (
          <div style={{ padding:'10px 14px', borderRadius:'12px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', marginBottom:'14px' }}>
            <p style={{ margin:0, fontSize:'13px', color:'#ef4444' }}>{error}</p>
          </div>
        )}

        <motion.button
          onClick={handleSubmit} disabled={!allDecided || submitting}
          whileHover={allDecided && !submitting ? { scale:1.02, y:-2 } : {}}
          whileTap={allDecided && !submitting ? { scale:0.97 } : {}}
          style={{ width:'100%', padding:'16px', borderRadius:'18px', border:'none', cursor: allDecided && !submitting ? 'pointer' : 'not-allowed', fontFamily:'inherit', fontWeight:800, fontSize:'16px', background: allDecided ? (needsReview.length > 0 ? 'linear-gradient(135deg,#f59e0b,#ef4444)' : 'linear-gradient(135deg,#16a34a,#15803d)') : 'rgba(15,23,42,0.06)', color: allDecided ? 'white' : '#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow: allDecided ? '0 8px 28px rgba(239,68,68,0.3)' : 'none', transition:'all 0.2s' }}>
          {submitting
            ? <><Loader style={{ width:'18px', height:'18px', animation:'spin 1s linear infinite' }} /> Enviando...</>
            : needsReview.length > 0
              ? <><Shield style={{ width:'18px', height:'18px' }} /> Solicitar autorización al instructor</>
              : <><CheckCircle2 style={{ width:'18px', height:'18px' }} /> Confirmar — podés volar</>
          }
        </motion.button>

        <p style={{ margin:'12px 0 0', textAlign:'center', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.2)':'#94a3b8' }}>
          Todas las decisiones quedan registradas con timestamp del servidor
        </p>
      </motion.div>

      {/* A6.2 parte 2B — Modal de override AOG (se abre cuando 409 AIRCRAFT_AOG o 503 AOG_CHECK_UNAVAILABLE) */}
      {aogBlock && (
        <AogOverrideModal
          isOpen={true}
          aogData={{
            ...aogBlock,
            aircraft: {
              registration: sessionData?.aircraft?.registration
                || sessionData?.aircraft?.name
                || aogBlock?.aircraft?.registration
                || '',
            },
          }}
          unavailable={!!aogBlock.unavailable}
          onConfirm={handleAogOverride}
          onCancel={() => setAogBlock(null)}
          darkMode={darkMode}
        />
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════
// PANTALLA DE ESPERA DE AUTORIZACIÓN
// ═════════════════════════════════════════════════════════════════
// Exportada para reuso desde el registro rápido (QuickOpenModal Parte 5+).
// Misma pantalla, mismo contador, mismo desenlace que en Valentina.
export const WaitingAuthScreen = ({ authId, authData, darkMode, pilotName, onAuthorized, onRetained, onOverride }) => {
  const [status, setStatus]         = useState(authData.status);
  const [instructorNote, setNote]   = useState('');
  const [timeLeft, setTimeLeft]     = useState(authData.timeout_minutes * 60);
  const [canOverride, setCanOverride] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/api/flight-auth/${authId}`);
        const auth = res.data.data;
        if (auth.status !== 'pending') {
          clearInterval(pollRef.current);
          setStatus(auth.status);
          setNote(auth.instructor_note || '');
          if (auth.status === 'authorized') onAuthorized(auth);
          if (auth.status === 'retained')   onRetained(auth);
        }
      } catch (_) {}
    }, POLLING_INTERVALS.flight_auth_pilot); 

    const countdown = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          setCanOverride(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(pollRef.current);
      clearInterval(countdown);
    };
  }, [authId]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const handleOverride = async () => {
    try {
      await api.put(`/api/flight-auth/${authId}/override`);
      onOverride();
    } catch (_) { onOverride(); }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background: darkMode?'#0d0f14':'linear-gradient(160deg,#eff6ff,#f0fdf4)' }}>
      <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }}
        style={{ maxWidth:'420px', width:'100%', background: darkMode?'rgba(15,23,42,0.96)':'white', backdropFilter:'blur(20px)', borderRadius:'28px', padding:'36px', boxShadow:'0 24px 60px rgba(0,0,0,0.15)', textAlign:'center' }}>

        <div style={{ position:'relative', width:'72px', height:'72px', margin:'0 auto 20px', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <motion.div animate={{ rotate:360 }} transition={{ duration:2, repeat:Infinity, ease:'linear' }}
            style={{ position:'absolute', inset:0, borderRadius:'50%', border:'3px solid transparent', borderTopColor:'#2563eb', borderRightColor:'#7c3aed' }} />
          <Clock style={{ width:'28px', height:'28px', color: darkMode?'#93c5fd':'#2563eb' }} />
        </div>

        <h2 style={{ margin:'0 0 8px', fontSize:'20px', fontWeight:800, color: darkMode?'white':'#0f172a' }}>
          Esperando al instructor
        </h2>
        <p style={{ margin:'0 0 20px', fontSize:'14px', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', lineHeight:1.6 }}>
          Se notificó al instructor sobre las anomalías. Esperá su respuesta antes de volar.
        </p>

        {timeLeft > 0 ? (
          <div style={{ padding:'16px', borderRadius:'16px', background: darkMode?'rgba(37,99,235,0.08)':'rgba(37,99,235,0.06)', border: darkMode?'1px solid rgba(37,99,235,0.15)':'1px solid rgba(37,99,235,0.12)', marginBottom:'20px' }}>
            <p style={{ margin:'0 0 4px', fontSize:'12px', fontWeight:600, color: darkMode?'rgba(147,197,253,0.7)':'#3b82f6', textTransform:'uppercase', letterSpacing:'0.06em' }}>Tiempo restante</p>
            <p style={{ margin:0, fontSize:'32px', fontWeight:900, color: darkMode?'white':'#0f172a', fontFamily:'monospace' }}>
              {String(minutes).padStart(2,'0')}:{String(seconds).padStart(2,'0')}
            </p>
          </div>
        ) : (
          <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
            style={{ padding:'14px', borderRadius:'16px', background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', marginBottom:'20px' }}>
            <p style={{ margin:0, fontSize:'13px', fontWeight:600, color:'#d97706' }}>
              ⏱ El instructor no respondió en el tiempo establecido
            </p>
          </motion.div>
        )}

        {canOverride && (
          <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}>
            <p style={{ margin:'0 0 12px', fontSize:'13px', color: darkMode?'rgba(255,255,255,0.45)':'#64748b', lineHeight:1.5 }}>
              Como Comandante de la aeronave, tenés la autoridad final sobre la decisión de vuelo.
            </p>
            <motion.button onClick={handleOverride}
              whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'14px', borderRadius:'16px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white', fontWeight:700, fontSize:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginBottom:'10px' }}>
              <Plane style={{ width:'16px', height:'16px' }} />
              Volar como Comandante
            </motion.button>
            <p style={{ margin:0, fontSize:'11px', color: darkMode?'rgba(255,255,255,0.2)':'#94a3b8' }}>
              Esta decisión queda registrada en el sistema
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════
// PANTALLA DE AVIÓN RETENIDO
// ═════════════════════════════════════════════════════════════════
const AircraftRetainedScreen = ({ authData, darkMode, onNewInspection }) => (
  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background: darkMode?'#0d0f14':'linear-gradient(160deg,#fef2f2,#fff7ed)' }}>
    <motion.div initial={{ scale:0.9, opacity:0 }} animate={{ scale:1, opacity:1 }}
      style={{ maxWidth:'420px', width:'100%', background: darkMode?'rgba(20,12,12,0.96)':'white', backdropFilter:'blur(16px)', borderRadius:'28px', padding:'36px', boxShadow:'0 24px 60px rgba(239,68,68,0.15)', textAlign:'center' }}>
      <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'rgba(239,68,68,0.1)', border:'2px solid rgba(239,68,68,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
        <AlertCircle style={{ width:'32px', height:'32px', color:'#ef4444' }} />
      </div>
      <h2 style={{ margin:'0 0 8px', fontSize:'22px', fontWeight:900, color:'#dc2626' }}>Avión retenido</h2>
      <p style={{ margin:'0 0 16px', fontSize:'14px', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', lineHeight:1.6 }}>
        El instructor decidió retener el avión. No podés volar hasta que el defecto sea revisado.
      </p>
      {authData.instructor_note && (
        <div style={{ padding:'12px 16px', borderRadius:'14px', background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.15)', marginBottom:'20px', textAlign:'left' }}>
          <p style={{ margin:'0 0 4px', fontSize:'11px', fontWeight:700, color:'#ef4444', textTransform:'uppercase', letterSpacing:'0.06em' }}>Nota del instructor</p>
          <p style={{ margin:0, fontSize:'14px', color: darkMode?'rgba(255,255,255,0.7)':'#374151' }}>{authData.instructor_note}</p>
        </div>
      )}
      <motion.button onClick={onNewInspection} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
        style={{ width:'100%', padding:'14px', borderRadius:'16px', border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid #e2e8f0', background:'transparent', cursor:'pointer', fontFamily:'inherit', fontWeight:600, fontSize:'14px', color: darkMode?'rgba(255,255,255,0.6)':'#64748b' }}>
        Volver al inicio
      </motion.button>
    </motion.div>
  </div>
);

// ═════════════════════════════════════════════════════════════════
// FIRMA DIGITAL
// ═════════════════════════════════════════════════════════════════
const SignaturePad = ({ onSave, onClear, darkMode }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * (canvas.width / rect.width),
        y: (e.touches[0].clientY - rect.top)  * (canvas.height / rect.height),
      };
    }
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = darkMode ? '#93c5fd' : '#1d4ed8';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDraw = (e) => {
    e.preventDefault();
    drawing.current = false;
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear();
  };

  const handleSave = () => {
    if (!hasSignature) return;
    const canvas   = canvasRef.current;
    const dataUrl  = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ position: 'relative', borderRadius: '14px', overflow: 'hidden', border: darkMode ? '1px solid rgba(99,179,237,0.3)' : '1px solid rgba(37,99,235,0.2)', background: darkMode ? 'rgba(15,25,50,0.6)' : 'rgba(248,250,252,1)' }}>
        <canvas
          ref={canvasRef}
          width={400} height={120}
          style={{ width: '100%', height: '100px', display: 'block', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
        {!hasSignature && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(37,99,235,0.3)', fontWeight: 600 }}>
              ✍️ Firmá acá con el dedo
            </span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={handleClear}
          style={{ flex: 1, padding: '8px', borderRadius: '10px', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', background: 'transparent', color: darkMode ? 'rgba(255,255,255,0.5)' : '#64748b', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          Borrar
        </button>
        <motion.button onClick={handleSave} disabled={!hasSignature}
          whileHover={hasSignature ? { scale: 1.02 } : {}}
          whileTap={hasSignature ? { scale: 0.97 } : {}}
          style={{ flex: 2, padding: '8px', borderRadius: '10px', border: 'none', background: hasSignature ? 'linear-gradient(135deg,#16a34a,#15803d)' : (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'), color: hasSignature ? 'white' : (darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8'), fontSize: '12px', fontWeight: 700, cursor: hasSignature ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
          {hasSignature ? t('confirm_signature',{ns:'admin'}) : t('sign_here',{ns:'admin'})}
        </motion.button>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════
// PDF Generator
// ═════════════════════════════════════════════════════════════════

const sanitizePDF = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFC')
    .replace(/[^\x20-\x7E\u00C0-\u00FF]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/_/g, '-')
    .trim();
};

const generatePDF = ({ sessionData, phasesData = [], checkedItems, skippedItems, hasPendingItems, uniquePending, observationItems = [], weatherData, pilotName, signatureData, t }) => {
  const doc = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18, contentW = pageW - margin*2;
  let y = 0;
  const addPage  = () => { doc.addPage(); y = margin; drawHeader(); };
  const checkY   = (n=10) => { if (y+n > pageH-margin) addPage(); };

  const drawHeader = () => {
    doc.setFillColor(37,99,235); doc.rect(0,0,pageW,14,'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(255,255,255);
    doc.text('CONDOR360 — GESTION AERONAUTICA', margin, 9.5);
    doc.text(`${sessionData.date||''} ${sessionData.time||''}`, pageW-margin, 9.5, { align:'right' });
    y = 22;
  };

  // ✅ v1.2 — Disclaimer aeronáutico completo en el footer del PDF
  const drawFooter = () => {
    const total = doc.internal.getNumberOfPages();
    for (let i=1; i<=total; i++) {
      doc.setPage(i);
      doc.setFillColor(245,247,250); doc.rect(0,pageH-13,pageW,13,'F');
      doc.setFont('helvetica','normal'); doc.setFontSize(6.5); doc.setTextColor(120,120,120);
      const disclaimerLine1 = 'Checklist generado por Condor360 - herramienta de asistencia administrativa. No reemplaza los procedimientos oficiales del fabricante (POH).';
      const disclaimerLine2 = 'La responsabilidad final del vuelo es del Piloto al Mando.';
      doc.text(disclaimerLine1, margin, pageH-5.5);
      doc.text(disclaimerLine2, margin, pageH-2.5);
      doc.text(`${i} / ${total}`, pageW-margin, pageH-3.5, { align:'right' });
    }
    doc.setTextColor(0,0,0);
  };

  const aircraft = sessionData.aircraft;
  const dateStr  = (sessionData.date||new Date().toISOString().split('T')[0]).replace(/-/g,'');
  const acStr    = `${aircraft?.manufacturer||'Aeronave'}_${aircraft?.model_name||''}`.replace(/\s+/g,'_');
  const downloadPDF = (filename) => {
    const pdfBlob = doc.output('blob');
    const url     = URL.createObjectURL(pdfBlob);
    const a       = document.createElement('a');
    a.href        = url;
    a.download    = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  drawHeader();

  // ═══════════════════════════════════════════════════════════════
  // Sub-etapa 3.2c-iii — BRANCH DEFENSIVO DE NO-REGRESIÓN.
  // Madryn (phasesPlan=['prevuelo']) y cualquier chequeo de 1 sola fase
  // caen SIEMPRE acá: es una copia literal del generatePDF pre-3.2c-iii,
  // sin ninguna referencia a phasesData — el PDF sale byte-equivalente
  // al de hoy (un checklist, una firma, sin encabezados de fase).
  // ═══════════════════════════════════════════════════════════════
  if (phasesData.length <= 1) {
  if (hasPendingItems) {
    doc.setFillColor(254,243,199); doc.roundedRect(margin,y,contentW,16,3,3,'F');
    doc.setDrawColor(251,191,36); doc.setLineWidth(0.5); doc.roundedRect(margin,y,contentW,16,3,3,'S');
    doc.setFont('helvetica','bold'); doc.setFontSize(13); doc.setTextColor(146,64,14);
    doc.text('CHEQUEO INCOMPLETO', pageW/2, y+10.5, { align:'center' }); 
  } else {
    doc.setFillColor(220,252,231); doc.roundedRect(margin,y,contentW,16,3,3,'F');
    doc.setDrawColor(134,239,172); doc.setLineWidth(0.5); doc.roundedRect(margin,y,contentW,16,3,3,'S');
    doc.setFont('helvetica','bold'); doc.setFontSize(13); doc.setTextColor(21,128,61);
    doc.text('CHEQUEO COMPLETO', pageW/2, y+10.5, { align:'center' }); 
  }
  y += 22;

  doc.setFillColor(248,250,252); doc.roundedRect(margin,y,contentW,40,3,3,'F');
  doc.setDrawColor(226,232,240); doc.roundedRect(margin,y,contentW,40,3,3,'S');
  const col1=margin+5, col2=pageW/2+2, lc=[100,116,139], vc=[15,23,42];
  const ir = (lbl,val,x,row) => {
    const ry=y+8+row*11;
    doc.setFont('helvetica','normal'); doc.setFontSize(7.5); doc.setTextColor(...lc); doc.text(lbl.toUpperCase(),x,ry);
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc); doc.text(sanitizePDF(val)||'-',x,ry+4.5);
  };
  const ac = sessionData.aircraft || sessionData.sessionData?.aircraft;
  const club = sessionData.clubName || sessionData.sessionData?.clubName || '—';
  ir('Aeronave',`${ac?.manufacturer||''} ${ac?.model_name||''}`,col1,0);
  ir(t('filter_registration',{ns:'admin'}),ac?.registration||'N/D',col2,0);
  ir('Aeroclub',club,col1,1);
  ir('Hora local',`${sessionData.date||''} ${sessionData.time||formatTime(new Date())}`,col2,1);
  ir('Piloto', pilotName || sessionData.pilotName || '—', col1, 2);
  y += 46;

  if (weatherData) {
    checkY(22);
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc);
    doc.text('CONDICIONES METEOROLÓGICAS', margin, y); y += 5;
    doc.setFillColor(239,246,255); doc.roundedRect(margin,y,contentW,18,3,3,'F');
    doc.setDrawColor(191,219,254); doc.roundedRect(margin,y,contentW,18,3,3,'S');
    const colW = contentW / 4;
    const wx = [margin+4, margin+colW+4, margin+colW*2+4, margin+colW*3+4];
    const wdata = [
      { label:'TEMP',      value: weatherData.temperature != null ? `${weatherData.temperature}°C` : '—' },
      { label:'QNH',       value: weatherData.qnh != null ? `${weatherData.qnh} hPa` : '—' },
      { label:'VIENTO',    value: weatherData.windSpeedKts != null ? `${weatherData.windSpeedKts} kt ${weatherData.windDirectionDeg ?? ''}°` : '—' },
      { label:t('weather_condition',{ns:'admin'}), value: weatherData.condition || '—' },
    ];
    wdata.forEach((w,i) => {
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lc); doc.text(w.label, wx[i], y+6);
      doc.setFont('helvetica','bold'); doc.setFontSize(8.5); doc.setTextColor(37,99,235); doc.text(String(w.value).substring(0,14), wx[i], y+13);
    });
    y += 24;
  }

  const total2=checkedItems.length+skippedItems.length;
  const stats=[
    {label:'Verificados', value:String(checkedItems.length),   color:[22,163,74]},
    {label:'Omitidos',    value:String(skippedItems.length),   color:[234,88,12]},
    {label:'Total',       value:String(total2),                color:[37,99,235]},
    {label:'Completado',  value:total2?`${Math.round((checkedItems.length/total2)*100)}%`:'0%', color:[124,58,237]},
  ];
  const stW=contentW/4-1.5;
  stats.forEach((s,i) => {
    const sx=margin+i*(stW+2);
    doc.setFillColor(248,250,252); doc.roundedRect(sx,y,stW,20,2,2,'F');
    doc.setDrawColor(226,232,240); doc.roundedRect(sx,y,stW,20,2,2,'S');
    doc.setFont('helvetica','bold'); doc.setFontSize(14); doc.setTextColor(...s.color);
    doc.text(s.value,sx+stW/2,y+11,{align:'center'});
    doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lc);
    doc.text(s.label.toUpperCase(),sx+stW/2,y+17,{align:'center'});
  });
  y += 26;

  // ── Combustible de salida (sólo si hay tracking) ─────────────
  const fuelStart  = sessionData?.fuelStart || null;
  const fuelLeft   = fuelStart && fuelStart.left  != null ? parseFloat(fuelStart.left)  : null;
  const fuelRight  = fuelStart && fuelStart.right != null ? parseFloat(fuelStart.right) : null;
  if (fuelLeft != null || fuelRight != null) {
    checkY(22);
    const fuelUnit = (fuelStart?.unit || 'L').toUpperCase();
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc);
    doc.text('COMBUSTIBLE DE SALIDA', margin, y); y += 5;
    doc.setFillColor(236,253,245); doc.roundedRect(margin,y,contentW,16,3,3,'F');
    doc.setDrawColor(167,243,208); doc.roundedRect(margin,y,contentW,16,3,3,'S');
    const colsCount = (fuelLeft != null && fuelRight != null) ? 3 : 1;
    const fc = [4, 120, 87];
    const lblc = [6, 95, 70];
    const fx  = (i) => margin + (contentW / colsCount) * i + 6;
    if (fuelLeft != null && fuelRight != null) {
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lblc);
      doc.text('IZQ',    fx(0), y + 6);
      doc.text('DER',    fx(1), y + 6);
      doc.text('TOTAL',  fx(2), y + 6);
      doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(...fc);
      doc.text(`${fuelLeft} ${fuelUnit}`,                 fx(0), y + 12);
      doc.text(`${fuelRight} ${fuelUnit}`,                fx(1), y + 12);
      doc.text(`${(fuelLeft + fuelRight).toFixed(1)} ${fuelUnit}`, fx(2), y + 12);
    } else {
      const total = (fuelLeft != null ? fuelLeft : 0) + (fuelRight != null ? fuelRight : 0);
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lblc);
      doc.text('TOTAL', fx(0), y + 6);
      doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(...fc);
      doc.text(`${total} ${fuelUnit}`, fx(0), y + 12);
    }
    y += 22;
  }

  if (sessionData.aircraft?.checklist) {
    doc.setFont('helvetica','bold'); doc.setFontSize(10); doc.setTextColor(...vc);
    doc.text('DETALLE DEL CHEQUEO', margin, y); y += 6;
    sessionData.aircraft.checklist.forEach((section, sectionIdx) => {
      checkY(14);
      doc.setFillColor(37,99,235); doc.roundedRect(margin,y,contentW,8,1.5,1.5,'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(255,255,255);
      doc.text(section.section.toUpperCase(), margin+4, y+5.5); y += 11;
      section.items.forEach((item,itemIdx) => {
        checkY(7);
        const txt = typeof item==='string' ? item : item.text;
        const isC = checkedItems.some(c=>c.section===sectionIdx&&c.item===itemIdx);
        const isS = skippedItems.some(s=>s.section===sectionIdx&&s.item===itemIdx);
        const obs = observationItems.find(o=>o.section===sectionIdx&&o.item===itemIdx);
        if (itemIdx%2===0){ doc.setFillColor(249,250,251); doc.rect(margin,y-1,contentW,obs?13:7,'F'); }
        if (isC) {
          doc.setFillColor(22,163,74); doc.circle(margin+4,y+2.5,2.5,'F');
          doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(255,255,255); // circulo verde lleno indica chequeado
        } else if (isS) {
          doc.setFillColor(234,88,12); doc.circle(margin+4,y+2.5,2.5,'F');
          doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(255,255,255); doc.text('x',margin+4,y+3.8,{align:'center'});
        } else if (obs) {
          doc.setFillColor(234,179,8); doc.circle(margin+4,y+2.5,2.5,'F');
          doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(255,255,255); doc.text('!',margin+4,y+3.8,{align:'center'});
        } else {
          doc.setDrawColor(156,163,175); doc.setLineWidth(0.4); doc.circle(margin+4,y+2.5,2.5,'S');
        }
        doc.setFont('helvetica','normal'); doc.setFontSize(8.5);
        doc.setTextColor(isS?156:30, isS?163:30, isS?175:30);
        doc.text(doc.splitTextToSize(sanitizePDF(txt), contentW-14)[0], margin+10, y+3.5);
        const isHobbs = (typeof item === 'object' && item?.item_type === 'hobbs') ||
                        (typeof txt === 'string' && txt.toLowerCase().includes('hor') && txt.toLowerCase().includes('metro'));
        const hobbsVal = isHobbs && sessionData?.hobbsMeter ? `${sessionData.hobbsMeter} hs` : null;
        const tg = hobbsVal ? hobbsVal : isC?'OK' : isS?t('omitted',{ns:'admin'}) : obs?t('observation',{ns:'admin'}) : t('pending_status',{ns:'admin'});
        const tc = isC?[220,252,231] : isS?[255,237,213] : obs?[254,243,199] : [243,244,246];
        const tt = isC?[21,128,61]   : isS?[154,52,18]   : obs?[146,64,14]   : [107,114,128];
        const tw = hobbsVal ? 28 : obs ? 22 : 18;
        doc.setFillColor(...tc); doc.roundedRect(pageW-margin-tw, y, tw, 5.5, 1,1,'F');
        doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(...tt);
        doc.text(tg, pageW-margin-tw/2, y+3.8, {align:'center'});
        y += 7;
        if (obs?.comment) {
          checkY(6);
          doc.setFont('helvetica','italic'); doc.setFontSize(7.5); doc.setTextColor(146,64,14);
          const commentLines = doc.splitTextToSize(`Obs: ${sanitizePDF(obs.comment)}`, contentW-16);
          commentLines.forEach(line => { doc.text(line, margin+12, y+3); y += 5; });
          y += 1;
        }
      });
      y += 3;
    });
  }

  if (observationItems.length > 0) {
    checkY(20); y += 4;
    doc.setFillColor(254,243,199); doc.roundedRect(margin, y, contentW, 8, 1.5,1.5,'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(146,64,14);
    doc.text(`[!] OBSERVACIONES REPORTADAS (${observationItems.length})`, margin+4, y+5.5); y += 11;
    observationItems.forEach((obs, idx) => {
      checkY(12);
      doc.setFillColor(255,251,235); doc.rect(margin, y-1, contentW, obs.comment ? 14 : 8, 'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(8.5); doc.setTextColor(146,64,14);
      doc.text(`${idx+1}.  ${sanitizePDF(obs.text)}`, margin+4, y+4);
      if (obs.comment) {
        doc.setFont('helvetica','italic'); doc.setFontSize(7.5); doc.setTextColor(180,83,9);
        doc.text(`   -> ${sanitizePDF(obs.comment)}`, margin+4, y+10); y += 15;
      } else { y += 9; }
    });
    y += 4;
  }

  if (hasPendingItems && uniquePending?.length > 0) {
    checkY(20); y += 4;
    doc.setFillColor(254,243,199); doc.roundedRect(margin,y,contentW,8,1.5,1.5,'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(146,64,14);
    doc.text(`[!] ITEMS PENDIENTES (${uniquePending.length})`, margin+4, y+5.5); y += 11;
    uniquePending.forEach((item,idx) => {
      checkY(7);
      doc.setFillColor(255,251,235); doc.rect(margin,y-1,contentW,7,'F');
      doc.setFont('helvetica','normal'); doc.setFontSize(8.5); doc.setTextColor(146,64,14);
      doc.text(`${idx+1}.  ${sanitizePDF(item.text)}`, margin+4, y+3.5); y += 7;
    });
    y += 4;
  }

  checkY(35); y += 6;
  doc.setDrawColor(226,232,240); doc.setLineWidth(0.4); doc.line(margin,y,pageW-margin,y); y += 8;
  doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(...vc);
  doc.text('Observaciones adicionales:', margin, y); y += 5;
  doc.setDrawColor(203,213,225); doc.setLineWidth(0.3);
  for (let i=0; i<3; i++) doc.line(margin, y+i*7, pageW-margin, y+i*7);
  y += 28;
  doc.line(margin, y, margin+60, y);
  doc.setFont('helvetica','normal'); doc.setFontSize(7.5); doc.setTextColor(...lc);
  doc.text('Firma del piloto', margin, y+4);
  if (signatureData) {
    try {
      doc.addImage(signatureData, 'PNG', margin, y - 28, 60, 26);
    } catch (_) {}
  }
  drawFooter();
  downloadPDF(`Condor360_Prevuelo_${acStr}_${dateStr}.pdf`);
  return;
  }

  // ═══════════════════════════════════════════════════════════════
  // Sub-etapa 3.2c-iii — BRANCH CONSOLIDADO (>1 fase).
  // Una sección por fase de phasesData (orden PHASE_ORDER), cada una con
  // su propio detalle de ítems, sus observaciones/omitidos y su firma
  // individual. Las fases salteadas se muestran como banner corto, sin
  // detalle ni firma.
  // ═══════════════════════════════════════════════════════════════
  const executedPhases  = phasesData.filter(p => !p.skipped);
  const globalHasPending = executedPhases.some(p => (p.skippedItems||[]).length > 0);

  if (globalHasPending) {
    doc.setFillColor(254,243,199); doc.roundedRect(margin,y,contentW,16,3,3,'F');
    doc.setDrawColor(251,191,36); doc.setLineWidth(0.5); doc.roundedRect(margin,y,contentW,16,3,3,'S');
    doc.setFont('helvetica','bold'); doc.setFontSize(13); doc.setTextColor(146,64,14);
    doc.text('CHEQUEO INCOMPLETO', pageW/2, y+10.5, { align:'center' });
  } else {
    doc.setFillColor(220,252,231); doc.roundedRect(margin,y,contentW,16,3,3,'F');
    doc.setDrawColor(134,239,172); doc.setLineWidth(0.5); doc.roundedRect(margin,y,contentW,16,3,3,'S');
    doc.setFont('helvetica','bold'); doc.setFontSize(13); doc.setTextColor(21,128,61);
    doc.text('CHEQUEO COMPLETO', pageW/2, y+10.5, { align:'center' });
  }
  y += 22;

  doc.setFillColor(248,250,252); doc.roundedRect(margin,y,contentW,40,3,3,'F');
  doc.setDrawColor(226,232,240); doc.roundedRect(margin,y,contentW,40,3,3,'S');
  const col1=margin+5, col2=pageW/2+2, lc=[100,116,139], vc=[15,23,42];
  const ir = (lbl,val,x,row) => {
    const ry=y+8+row*11;
    doc.setFont('helvetica','normal'); doc.setFontSize(7.5); doc.setTextColor(...lc); doc.text(lbl.toUpperCase(),x,ry);
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc); doc.text(sanitizePDF(val)||'-',x,ry+4.5);
  };
  const ac = sessionData.aircraft || sessionData.sessionData?.aircraft;
  const club = sessionData.clubName || sessionData.sessionData?.clubName || '—';
  ir('Aeronave',`${ac?.manufacturer||''} ${ac?.model_name||''}`,col1,0);
  ir(t('filter_registration',{ns:'admin'}),ac?.registration||'N/D',col2,0);
  ir('Aeroclub',club,col1,1);
  ir('Hora local',`${sessionData.date||''} ${sessionData.time||formatTime(new Date())}`,col2,1);
  ir('Piloto', pilotName || sessionData.pilotName || '—', col1, 2);
  y += 46;

  if (weatherData) {
    checkY(22);
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc);
    doc.text('CONDICIONES METEOROLÓGICAS', margin, y); y += 5;
    doc.setFillColor(239,246,255); doc.roundedRect(margin,y,contentW,18,3,3,'F');
    doc.setDrawColor(191,219,254); doc.roundedRect(margin,y,contentW,18,3,3,'S');
    const colW = contentW / 4;
    const wx = [margin+4, margin+colW+4, margin+colW*2+4, margin+colW*3+4];
    const wdata = [
      { label:'TEMP',      value: weatherData.temperature != null ? `${weatherData.temperature}°C` : '—' },
      { label:'QNH',       value: weatherData.qnh != null ? `${weatherData.qnh} hPa` : '—' },
      { label:'VIENTO',    value: weatherData.windSpeedKts != null ? `${weatherData.windSpeedKts} kt ${weatherData.windDirectionDeg ?? ''}°` : '—' },
      { label:t('weather_condition',{ns:'admin'}), value: weatherData.condition || '—' },
    ];
    wdata.forEach((w,i) => {
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lc); doc.text(w.label, wx[i], y+6);
      doc.setFont('helvetica','bold'); doc.setFontSize(8.5); doc.setTextColor(37,99,235); doc.text(String(w.value).substring(0,14), wx[i], y+13);
    });
    y += 24;
  }

  const totalChecked  = executedPhases.reduce((a,p)=>a+(p.checkedItems||[]).length,0);
  const totalSkipped  = executedPhases.reduce((a,p)=>a+(p.skippedItems||[]).length,0);
  const totalItemsAll = totalChecked + totalSkipped;
  const stats=[
    {label:'Verificados', value:String(totalChecked),   color:[22,163,74]},
    {label:'Omitidos',    value:String(totalSkipped),   color:[234,88,12]},
    {label:'Total',       value:String(totalItemsAll),                color:[37,99,235]},
    {label:'Completado',  value:totalItemsAll?`${Math.round((totalChecked/totalItemsAll)*100)}%`:'0%', color:[124,58,237]},
  ];
  const stW=contentW/4-1.5;
  stats.forEach((s,i) => {
    const sx=margin+i*(stW+2);
    doc.setFillColor(248,250,252); doc.roundedRect(sx,y,stW,20,2,2,'F');
    doc.setDrawColor(226,232,240); doc.roundedRect(sx,y,stW,20,2,2,'S');
    doc.setFont('helvetica','bold'); doc.setFontSize(14); doc.setTextColor(...s.color);
    doc.text(s.value,sx+stW/2,y+11,{align:'center'});
    doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lc);
    doc.text(s.label.toUpperCase(),sx+stW/2,y+17,{align:'center'});
  });
  y += 26;

  // ── Combustible de salida (sólo si hay tracking, se toma en prevuelo) ──
  const fuelStart  = sessionData?.fuelStart || null;
  const fuelLeft   = fuelStart && fuelStart.left  != null ? parseFloat(fuelStart.left)  : null;
  const fuelRight  = fuelStart && fuelStart.right != null ? parseFloat(fuelStart.right) : null;
  if (fuelLeft != null || fuelRight != null) {
    checkY(22);
    const fuelUnit = (fuelStart?.unit || 'L').toUpperCase();
    doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc);
    doc.text('COMBUSTIBLE DE SALIDA', margin, y); y += 5;
    doc.setFillColor(236,253,245); doc.roundedRect(margin,y,contentW,16,3,3,'F');
    doc.setDrawColor(167,243,208); doc.roundedRect(margin,y,contentW,16,3,3,'S');
    const colsCount = (fuelLeft != null && fuelRight != null) ? 3 : 1;
    const fc = [4, 120, 87];
    const lblc = [6, 95, 70];
    const fx  = (i) => margin + (contentW / colsCount) * i + 6;
    if (fuelLeft != null && fuelRight != null) {
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lblc);
      doc.text('IZQ',    fx(0), y + 6);
      doc.text('DER',    fx(1), y + 6);
      doc.text('TOTAL',  fx(2), y + 6);
      doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(...fc);
      doc.text(`${fuelLeft} ${fuelUnit}`,                 fx(0), y + 12);
      doc.text(`${fuelRight} ${fuelUnit}`,                fx(1), y + 12);
      doc.text(`${(fuelLeft + fuelRight).toFixed(1)} ${fuelUnit}`, fx(2), y + 12);
    } else {
      const total = (fuelLeft != null ? fuelLeft : 0) + (fuelRight != null ? fuelRight : 0);
      doc.setFont('helvetica','normal'); doc.setFontSize(7); doc.setTextColor(...lblc);
      doc.text('TOTAL', fx(0), y + 6);
      doc.setFont('helvetica','bold'); doc.setFontSize(11); doc.setTextColor(...fc);
      doc.text(`${total} ${fuelUnit}`, fx(0), y + 12);
    }
    y += 22;
  }

  // ── Por cada fase del plan, en orden PHASE_ORDER ──────────────
  const orderedPhases = PHASE_ORDER.map(ph => phasesData.find(p => p.phase === ph)).filter(Boolean);

  orderedPhases.forEach(p => {
    const phaseTitle = PHASE_SECTION_TITLES[p.phase] || (p.phase||'').toUpperCase();
    checkY(16);
    doc.setFillColor(15,23,42); doc.roundedRect(margin,y,contentW,10,2,2,'F');
    doc.setFont('helvetica','bold'); doc.setFontSize(10); doc.setTextColor(255,255,255);
    doc.text(phaseTitle, margin+4, y+7);
    if (p.timestamp) {
      const hora = new Date(p.timestamp).toLocaleTimeString('es-AR', { hour:'2-digit', minute:'2-digit' });
      doc.text(hora, pageW-margin-4, y+7, { align:'right' });
    }
    y += 14;

    if (p.skipped) {
      checkY(14);
      doc.setFillColor(254,243,199); doc.roundedRect(margin,y,contentW,12,2,2,'F');
      doc.setDrawColor(251,191,36); doc.setLineWidth(0.4); doc.roundedRect(margin,y,contentW,12,2,2,'S');
      doc.setFont('helvetica','italic'); doc.setFontSize(8.5); doc.setTextColor(146,64,14);
      doc.text('Fase salteada — opcional para este aeroclub', margin+4, y+7.5);
      y += 18;
      return;
    }

    const pChecklist = p.checklist || [];
    const pChecked   = p.checkedItems || [];
    const pSkipped   = p.skippedItems || [];
    const pObs       = p.observationItems || [];

    if (pChecklist.length > 0) {
      doc.setFont('helvetica','bold'); doc.setFontSize(9); doc.setTextColor(...vc);
      doc.text('DETALLE DEL CHEQUEO', margin, y); y += 6;
      pChecklist.forEach((section, sectionIdx) => {
        checkY(14);
        doc.setFillColor(37,99,235); doc.roundedRect(margin,y,contentW,8,1.5,1.5,'F');
        doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(255,255,255);
        doc.text(section.section.toUpperCase(), margin+4, y+5.5); y += 11;
        section.items.forEach((item,itemIdx) => {
          checkY(7);
          const txt = typeof item==='string' ? item : item.text;
          const isC = pChecked.some(c=>c.section===sectionIdx&&c.item===itemIdx);
          const isS = pSkipped.some(s=>s.section===sectionIdx&&s.item===itemIdx);
          const obs = pObs.find(o=>o.section===sectionIdx&&o.item===itemIdx);
          if (itemIdx%2===0){ doc.setFillColor(249,250,251); doc.rect(margin,y-1,contentW,obs?13:7,'F'); }
          if (isC) {
            doc.setFillColor(22,163,74); doc.circle(margin+4,y+2.5,2.5,'F');
            doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(255,255,255);
          } else if (isS) {
            doc.setFillColor(234,88,12); doc.circle(margin+4,y+2.5,2.5,'F');
            doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(255,255,255); doc.text('x',margin+4,y+3.8,{align:'center'});
          } else if (obs) {
            doc.setFillColor(234,179,8); doc.circle(margin+4,y+2.5,2.5,'F');
            doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(255,255,255); doc.text('!',margin+4,y+3.8,{align:'center'});
          } else {
            doc.setDrawColor(156,163,175); doc.setLineWidth(0.4); doc.circle(margin+4,y+2.5,2.5,'S');
          }
          doc.setFont('helvetica','normal'); doc.setFontSize(8.5);
          doc.setTextColor(isS?156:30, isS?163:30, isS?175:30);
          doc.text(doc.splitTextToSize(sanitizePDF(txt), contentW-14)[0], margin+10, y+3.5);
          const isHobbs = (typeof item === 'object' && item?.item_type === 'hobbs') ||
                          (typeof txt === 'string' && txt.toLowerCase().includes('hor') && txt.toLowerCase().includes('metro'));
          const hobbsVal = isHobbs && sessionData?.hobbsMeter ? `${sessionData.hobbsMeter} hs` : null;
          const tg = hobbsVal ? hobbsVal : isC?'OK' : isS?t('omitted',{ns:'admin'}) : obs?t('observation',{ns:'admin'}) : t('pending_status',{ns:'admin'});
          const tc = isC?[220,252,231] : isS?[255,237,213] : obs?[254,243,199] : [243,244,246];
          const tt = isC?[21,128,61]   : isS?[154,52,18]   : obs?[146,64,14]   : [107,114,128];
          const tw = hobbsVal ? 28 : obs ? 22 : 18;
          doc.setFillColor(...tc); doc.roundedRect(pageW-margin-tw, y, tw, 5.5, 1,1,'F');
          doc.setFont('helvetica','bold'); doc.setFontSize(6); doc.setTextColor(...tt);
          doc.text(tg, pageW-margin-tw/2, y+3.8, {align:'center'});
          y += 7;
          if (obs?.comment) {
            checkY(6);
            doc.setFont('helvetica','italic'); doc.setFontSize(7.5); doc.setTextColor(146,64,14);
            const commentLines = doc.splitTextToSize(`Obs: ${sanitizePDF(obs.comment)}`, contentW-16);
            commentLines.forEach(line => { doc.text(line, margin+12, y+3); y += 5; });
            y += 1;
          }
        });
        y += 3;
      });
    }

    if (pObs.length > 0) {
      checkY(20); y += 4;
      doc.setFillColor(254,243,199); doc.roundedRect(margin, y, contentW, 8, 1.5,1.5,'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(146,64,14);
      doc.text(`[!] OBSERVACIONES REPORTADAS (${pObs.length})`, margin+4, y+5.5); y += 11;
      pObs.forEach((obs, idx) => {
        checkY(12);
        doc.setFillColor(255,251,235); doc.rect(margin, y-1, contentW, obs.comment ? 14 : 8, 'F');
        doc.setFont('helvetica','bold'); doc.setFontSize(8.5); doc.setTextColor(146,64,14);
        doc.text(`${idx+1}.  ${sanitizePDF(obs.text)}`, margin+4, y+4);
        if (obs.comment) {
          doc.setFont('helvetica','italic'); doc.setFontSize(7.5); doc.setTextColor(180,83,9);
          doc.text(`   -> ${sanitizePDF(obs.comment)}`, margin+4, y+10); y += 15;
        } else { y += 9; }
      });
      y += 4;
    }

    if (pSkipped.length > 0) {
      checkY(20); y += 4;
      doc.setFillColor(254,243,199); doc.roundedRect(margin,y,contentW,8,1.5,1.5,'F');
      doc.setFont('helvetica','bold'); doc.setFontSize(8); doc.setTextColor(146,64,14);
      doc.text(`[!] ITEMS OMITIDOS EN ESTA FASE (${pSkipped.length})`, margin+4, y+5.5); y += 11;
      pSkipped.forEach((item,idx) => {
        checkY(7);
        doc.setFillColor(255,251,235); doc.rect(margin,y-1,contentW,7,'F');
        doc.setFont('helvetica','normal'); doc.setFontSize(8.5); doc.setTextColor(146,64,14);
        doc.text(`${idx+1}.  ${sanitizePDF(item.text)}`, margin+4, y+3.5); y += 7;
      });
      y += 4;
    }

    // Firma individual de esta fase.
    checkY(40); y += 8;
    doc.setDrawColor(226,232,240); doc.setLineWidth(0.4); doc.line(margin,y,pageW-margin,y); y += 28;
    doc.line(margin, y, margin+60, y);
    doc.setFont('helvetica','normal'); doc.setFontSize(7.5); doc.setTextColor(...lc);
    doc.text(`Firma del piloto — ${phaseTitle}`, margin, y+4);
    if (p.signatureData) {
      try { doc.addImage(p.signatureData, 'PNG', margin, y - 28, 60, 26); } catch (_) {}
    }
    y += 14;
  });

  drawFooter();
  downloadPDF(`Condor360_Chequeo_${acStr}_${dateStr}.pdf`);
};

// ═════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════════════════════════
const CompletionScreen = ({
  hasPendingItems, uniquePending, onReviewSkipped, onNewInspection,
  sessionData, checkedItems, skippedItems, darkMode, weatherData,
  observationItems = [], pilotName, orgFeatures,
  activeInspectionId,
  // Multi-fase (Sub-etapa 3.2b). Default isLastPhase=true reproduce el
  // comportamiento pre-3.2b si algún caller no las pasa (defensivo).
  isLastPhase = true, activePhase = 'prevuelo', nextPhase = null, onContinuePhase,
  // Obligatorias/opcionales (Sub-etapa 3.2c-ii). Default false/no-op
  // reproduce el comportamiento pre-3.2c-ii si algún caller no las pasa.
  isNextPhaseOptional = false, onSkipPhase,
  // Sub-etapa 3.2c-iii — snapshots de fases ya cerradas ([] por defecto,
  // reproduce el comportamiento pre-3.2c-iii si algún caller no los pasa)
  // y el checklist REAL de la fase activa (no sessionData.aircraft.checklist,
  // que queda congelado en el checklist de la fase 1 en chequeos multi-fase).
  phasesData = [], activePhaseChecklist,
}) => {
  const { t } = useTranslation();
  const { t: tAdmin } = useTranslation('admin');
  const { saveInspection, isSaving, saveError, saveSuccess, savedOffline } = useInspection();
  const [localSaveComplete, setLocalSaveComplete] = useState(false);
  const [showRetry,     setShowRetry]     = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [offlineMode,   setOfflineMode]   = useState(false);
  const [pdfExported,   setPdfExported]   = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [showPendingConfirm, setShowPendingConfirm] = useState(false);

  const [authFlow, setAuthFlow]     = useState('checklist');
  const [authId, setAuthId]         = useState(null);
  const [authData, setAuthData]     = useState(null);
  const [savedInspectionId, setSavedInspectionId] = useState(null);

  useEffect(() => {
    if (!saveSuccess && !isSaving && !saveError) handleAutoSave();
    const handleSWMessage = (event) => {
      if (event.data?.type === 'INSPECTION_SYNCED') {
        setLocalSaveComplete(true);
        setOfflineMode(false);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
  }, []);

  const handleAutoSave = async () => {
    if (!navigator.onLine) { setOfflineMode(true); setShowRetry(true); return; }
    try {
      const aircraftId = sessionData.aircraft?.id;
      if (!aircraftId) throw new Error('Aircraft ID is missing.');
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(aircraftId)) throw new Error(`Invalid aircraft ID: ${aircraftId}`);

      const result = await saveInspection({
        aircraft_model_id: aircraftId,
        inspection_date:   sessionData.date || new Date().toISOString().split('T')[0],
        inspection_time:   formatTime(new Date()),
        club_name:         sessionData.clubName,
        hobbs_meter:       sessionData.hobbsMeter || null,
        location_lat:      sessionData.location?.lat || null,
        location_lon:      sessionData.location?.lon || null,
        weather: weatherData ? {
          temp_celsius:      weatherData.temperature,
          qnh_hpa:           weatherData.qnh,
          wind_speed_kts:    weatherData.windSpeedKts,
          wind_direction_deg: weatherData.windDirectionDeg,
          condition:         weatherData.condition,
          humidity:          weatherData.humidity,
        } : null,
        items:   prepareInspectionItems(),
        // Fix bug item_text incorrecto en fases 2/3: el checklist de la fase
        // activa es activePhaseChecklist, no sessionData.aircraft.checklist
        // (ese queda congelado en la fase 1 — ver bug-item-text-multifase.md).
        status:  (hasPendingItems || checkedItems.length < ((activePhaseChecklist || sessionData?.aircraft?.checklist)?.reduce((acc,s)=>acc+s.items.length,0)||0)) ? 'incomplete' : 'completed',
        remarks: hasPendingItems
          ? `Inspección completada con ${uniquePending.length} ítems pendientes`
          : checkedItems.length < ((activePhaseChecklist || sessionData?.aircraft?.checklist)?.reduce((acc,s)=>acc+s.items.length,0)||0)
          ? `Inspección incompleta: ${checkedItems.length} ítems chequeados`
          : t('inspection_completed', {ns:'admin'}),
        inspection_id: activeInspectionId || null,
      });

      setLocalSaveComplete(true);
      if (result?.inspection?.id) setSavedInspectionId(result.inspection.id);

      if (observationItems.length > 0 && !hasPendingItems) {
        setAuthFlow('classification');
      }

      if (observationItems.length > 0) {
        window.dispatchEvent(new CustomEvent('condor360:refresh'));
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      setOfflineMode(!navigator.onLine);
      setShowRetry(true);
    }
  };

  const prepareInspectionItems = () => {
    const items = [];
    // Fix bug item_text incorrecto en fases 2/3: usar activePhaseChecklist
    // (checklist real de la fase activa), con fallback defensivo a
    // sessionData.aircraft.checklist por si algún caller viejo no lo pasa.
    const checklistForSave = activePhaseChecklist || sessionData.aircraft?.checklist;
    if (checklistForSave) {
      checklistForSave.forEach((section, sI) => {
        section.items.forEach((item, iI) => {
          const isChecked = checkedItems.some(c => c.section===sI && c.item===iI);
          const isSkipped = skippedItems.some(s => s.section===sI && s.item===iI);
          const obs       = observationItems.find(o => o.section===sI && o.item===iI);
          const status    = obs ? 'observation' : isChecked ? 'checked' : isSkipped ? 'skipped' : 'pending';
          items.push({
            section_name: section.section, section_order: sI,
            item_text: typeof item==='string' ? item : item.text, item_order: iI,
            status, notes: obs?.comment || null, photos: [],
          });
        });
      });
    }
    return items;
  };

  const handleExportPDF = async () => {
    setPdfGenerating(true);
    try {
      await new Promise(r => setTimeout(r, 200));
      // Sub-etapa 3.2c-iii — la fase que se está cerrando ahora mismo (la
      // que muestra esta pantalla) todavía no tiene snapshot en phasesData
      // (eso ocurre en App.js cuando el piloto avanza a la próxima fase, o
      // nunca si ésta es la última). Se agrega acá, en el momento del
      // export, con el checklist REAL de la fase activa y la firma
      // capturada en esta misma pantalla.
      const currentPhaseSnapshot = {
        phase: activePhase,
        checklist: activePhaseChecklist || sessionData?.aircraft?.checklist || [],
        checkedItems, skippedItems, observationItems,
        signatureData: signatureData || null,
        timestamp: new Date().toISOString(),
      };
      const allPhasesData = [...phasesData, currentPhaseSnapshot];
      generatePDF({ sessionData, phasesData: allPhasesData, checkedItems, skippedItems, hasPendingItems, uniquePending, observationItems, weatherData, pilotName, signatureData, t });
      setPdfExported(true);
    } catch(err) { console.error('PDF error:', err); }
    finally { setPdfGenerating(false); }
  };

  const handleShareWhatsApp = () => {
    const aircraft  = sessionData?.aircraft ? `${sessionData.aircraft.manufacturer || ''} ${sessionData.aircraft.model_name || ''}`.trim() : 'Aeronave';
    const reg       = sessionData?.aircraft?.registration ? ` (${sessionData.aircraft.registration})` : '';
    const pilot     = pilotName || sessionData?.pilotName || sessionData?.user?.name || 'Piloto';
    const date      = sessionData?.date ? new Date(sessionData.date + 'T00:00:00').toLocaleDateString('es-AR') : new Date().toLocaleDateString('es-AR');
    const time      = sessionData?.time || formatTime(new Date());
    const total     = checkedItems.length + skippedItems.length;
    const pct       = total ? Math.round((checkedItems.length/total)*100) : 0;
    const obsCount  = observationItems.length;
    const statusIcon = hasPendingItems ? '⚠️' : '✅';
    const statusText = hasPendingItems ? 'INCOMPLETO' : 'COMPLETADO';
    const lines = [
      `${statusIcon} *CONDOR360 - CHEQUEO ${statusText}*`, ``,
      `✈️  *Avion: ${aircraft}${reg}*`,
      `👨‍✈️  *Piloto: ${pilot}*`,
      `📅  *Fecha: ${date}  ${time}*`, ``,
      `📊  *${checkedItems.length}/${total} ítems chequeados (${pct}%)*`,
      (() => {
        const fs = sessionData?.fuelStart;
        if (!fs) return null;
        const fl = fs.left  != null ? parseFloat(fs.left)  : null;
        const fr = fs.right != null ? parseFloat(fs.right) : null;
        if (fl == null && fr == null) return null;
        const unit = (fs.unit || 'L').toUpperCase();
        if (fl != null && fr != null)
          return `⛽  *Combustible salida: Izq ${fl} · Der ${fr} · Total ${(fl + fr).toFixed(1)} ${unit}*`;
        return `⛽  *Combustible salida: ${fl != null ? fl : fr} ${unit}*`;
      })(),
      skippedItems.length > 0 ? `⚠️  *${skippedItems.length} ítem${skippedItems.length > 1 ? 's' : ''} omitido${skippedItems.length > 1 ? 's' : ''}*` : null,
      ...(obsCount > 0 ? [
        `📝  *${obsCount} observación${obsCount > 1 ? 'es' : ''} registrada${obsCount > 1 ? 's' : ''}:*`,
        ...observationItems.map((o, i) => `   *${i + 1}. ${o.text}*${o.comment ? ` — _${o.comment}_` : ''}`),
      ] : []),
      ``, `📍 *${sessionData?.clubName || 'Aeroclub'}*`,
    ].filter(l => l !== null).join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(lines)}`, '_blank');
  };

  const totalItems = checkedItems.length + skippedItems.length;
  const pct = totalItems ? Math.round((checkedItems.length/totalItems)*100) : 0;
  const successBg = darkMode
    ? 'linear-gradient(160deg, #060d08 0%, #080e10 50%, #060d08 100%)'
    : 'linear-gradient(160deg, #ecfdf5 0%, #f0fdf4 40%, #eff6ff 100%)';

  if (authFlow === 'classification') {
    return (
      <AnomalyClassificationScreen
        observationItems={observationItems}
        sessionData={sessionData}
        inspectionId={savedInspectionId}
        darkMode={darkMode}
        pilotName={pilotName}
        onComplete={(result) => {
          if (result.auto_authorized) {
            setAuthFlow('authorized');
          } else {
            setAuthId(result.data.id);
            setAuthData(result.data);
            setAuthFlow('waiting');
          }
        }}
      />
    );
  }

  if (authFlow === 'waiting' && authId) {
    return (
      <WaitingAuthScreen
        authId={authId}
        authData={authData}
        darkMode={darkMode}
        pilotName={pilotName}
        onAuthorized={(auth) => { setAuthData(auth); setAuthFlow('authorized'); }}
        onRetained={(auth)   => { setAuthData(auth); setAuthFlow('retained');   }}
        onOverride={()       => setAuthFlow('override')}
      />
    );
  }

  if (authFlow === 'retained') {
    return (
      <AircraftRetainedScreen
        authData={authData}
        darkMode={darkMode}
        onNewInspection={onNewInspection}
      />
    );
  }

  if (isSaving) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background: darkMode?'#0d0f14':successBg }}>
        <motion.div initial={{ scale:0.8, opacity:0 }} animate={{ scale:1, opacity:1 }} transition={{ type:'spring' }}
          style={{ textAlign:'center', background: darkMode?'rgba(15,25,20,0.9)':'rgba(255,255,255,0.9)', backdropFilter:'blur(20px)', borderRadius:'28px', padding:'48px 40px', boxShadow:'0 20px 60px rgba(0,0,0,0.15)', maxWidth:'360px', width:'100%' }}>
          <div style={{ position:'relative', width:'64px', height:'64px', margin:'0 auto 20px', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <motion.div animate={{ rotate:360 }} transition={{ duration:1.5, repeat:Infinity, ease:'linear' }}
              style={{ position:'absolute', inset:0, borderRadius:'50%', border:'3px solid transparent', borderTopColor:'#22c55e', borderRightColor:'#3b82f6' }} />
            <Save style={{ width:'24px', height:'24px', color: darkMode?'#4ade80':'#16a34a' }} />
          </div>
          <h2 style={{ margin:'0 0 8px', fontSize:'20px', fontWeight:800, color: darkMode?'rgba(255,255,255,0.9)':'#0f172a' }}>Guardando...</h2>
          <p style={{ margin:0, fontSize:'13px', color: darkMode?'rgba(255,255,255,0.4)':'#64748b' }}>Sincronizando con el servidor</p>
        </motion.div>
      </div>
    );
  }

  if (savedOffline || saveError || showRetry) {
    if (savedOffline || offlineMode || !navigator.onLine) {
      return (
        <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background: darkMode?'#0d0f14':'linear-gradient(135deg,#eff6ff,#f0fdf4)' }}>
          <motion.div initial={{ scale:0.85, opacity:0 }} animate={{ scale:1, opacity:1 }}
            style={{ textAlign:'center', background: darkMode?'rgba(12,20,12,0.9)':'white', backdropFilter:'blur(16px)', borderRadius:'28px', padding:'40px', boxShadow:'0 20px 60px rgba(0,0,0,0.12)', maxWidth:'420px', width:'100%' }}>
            <motion.div animate={{ scale:[1,1.08,1] }} transition={{ duration:2, repeat:Infinity }}
              style={{ width:'72px', height:'72px', borderRadius:'50%', background:'rgba(245,158,11,0.1)', border:'2px solid rgba(245,158,11,0.3)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
              <WifiOff style={{ width:'32px', height:'32px', color:'#d97706' }} />
            </motion.div>
            <h2 style={{ margin:'0 0 8px', fontSize:'20px', fontWeight:800, color: darkMode?'rgba(255,255,255,0.9)':'#0f172a' }}>Sin conexión</h2>
            <p style={{ margin:'0 0 6px', fontSize:'14px', color: darkMode?'rgba(255,255,255,0.5)':'#64748b', lineHeight:1.6 }}>
              El chequeo está <strong>guardado localmente</strong>. Se sincronizará automáticamente cuando recuperes internet.
            </p>
            <p style={{ margin:'0 0 20px', fontSize:'13px', color: darkMode?'#fcd34d':'#92400e', fontWeight:600 }}>
              📋 Exportá el PDF ahora como respaldo.
            </p>
            <motion.button onClick={handleExportPDF} disabled={pdfGenerating}
              whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'17px', background: pdfExported ? 'linear-gradient(135deg,#16a34a,#15803d)' : 'linear-gradient(135deg,#2563eb,#7c3aed)', color:'white', border:'none', borderRadius:'18px', fontWeight:800, fontSize:'16px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow:'0 8px 28px rgba(37,99,235,0.35)', marginBottom:'10px' }}>
              {pdfGenerating ? <><Loader style={{ width:'18px', height:'18px', animation:'spin 1s linear infinite' }} /> Generando...</> : pdfExported ? <><CheckCircle2 style={{ width:'18px', height:'18px' }} /> PDF exportado ✓</> : <><FileDown style={{ width:'18px', height:'18px' }} /> Exportar PDF (recomendado)</>}
            </motion.button>
            <motion.button onClick={pdfExported ? onNewInspection : undefined}
              style={{ width:'100%', padding:'14px', background: 'transparent', color: pdfExported ? (darkMode?'rgba(255,255,255,0.6)':'#64748b') : (darkMode?'rgba(255,255,255,0.2)':'#cbd5e1'), border: `1px solid ${pdfExported ? (darkMode?'rgba(255,255,255,0.1)':'rgba(15,23,42,0.1)') : (darkMode?'rgba(255,255,255,0.05)':'rgba(15,23,42,0.05)')}`, borderRadius:'16px', fontWeight:600, fontSize:'14px', cursor: pdfExported ? 'pointer' : 'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
              <PlusCircle style={{ width:'15px', height:'15px' }} />
              {pdfExported ? t('new_inspection',{ns:'admin'}) : t('new_inspection_export_first',{ns:'admin'})}
            </motion.button>
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.5 }}
              style={{ marginTop:'16px', padding:'8px 12px', borderRadius:'12px', background: darkMode?'rgba(59,130,246,0.08)':'rgba(59,130,246,0.06)', border: darkMode?'1px solid rgba(59,130,246,0.15)':'1px solid rgba(59,130,246,0.12)', display:'flex', alignItems:'center', justifyContent:'center', gap:'7px' }}>
              <motion.div animate={{ opacity:[0.4,1,0.4] }} transition={{ duration:2, repeat:Infinity }}
                style={{ width:'7px', height:'7px', borderRadius:'50%', background:'#3b82f6', flexShrink:0 }} />
              <span style={{ fontSize:'11px', fontWeight:600, color: darkMode?'#93c5fd':'#2563eb' }}>Background Sync activo</span>
            </motion.div>
          </motion.div>
        </div>
      );
    }

    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background: darkMode?'#0d0f14':'linear-gradient(135deg,#fef2f2,#fff7ed)' }}>
        <motion.div initial={{ scale:0.85, opacity:0 }} animate={{ scale:1, opacity:1 }}
          style={{ textAlign:'center', background: darkMode?'rgba(20,12,12,0.9)':'white', backdropFilter:'blur(16px)', borderRadius:'28px', padding:'40px', boxShadow:'0 20px 60px rgba(0,0,0,0.12)', maxWidth:'400px', width:'100%' }}>
          <div style={{ width:'72px', height:'72px', borderRadius:'50%', background:'rgba(239,68,68,0.1)', border:'2px solid rgba(239,68,68,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
            <AlertCircle style={{ width:'32px', height:'32px', color:'#ef4444' }} />
          </div>
          <h2 style={{ margin:'0 0 8px', fontSize:'20px', fontWeight:800, color:'#dc2626' }}>Error al guardar</h2>
          <p style={{ margin:'0 0 24px', fontSize:'14px', color: darkMode?'rgba(255,255,255,0.5)':'#64748b' }}>{saveError||'No se pudo guardar la inspección.'}</p>
          <div style={{ display:'flex', gap:'10px' }}>
            <motion.button onClick={handleAutoSave} whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
              style={{ flex:1, padding:'14px', background:'#ef4444', color:'white', border:'none', borderRadius:'16px', fontWeight:700, fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
              <RefreshCw style={{ width:'15px', height:'15px' }} /> Reintentar
            </motion.button>
            <motion.button onClick={handleExportPDF} disabled={pdfGenerating} whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
              style={{ flex:1, padding:'14px', background:'#3b82f6', color:'white', border:'none', borderRadius:'16px', fontWeight:700, fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
              <FileDown style={{ width:'15px', height:'15px' }} /> PDF
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (hasPendingItems) {
    const warnBg = darkMode
      ? 'linear-gradient(160deg, #130a00 0%, #1a0c0c 50%, #130a00 100%)'
      : 'linear-gradient(160deg, #fffbeb 0%, #fff7ed 40%, #fef2f2 100%)';
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:warnBg, position:'relative', overflow:'hidden' }}>
        <style>{`@keyframes cs-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} @keyframes warn-pulse{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.4)}50%{box-shadow:0 0 0 16px rgba(239,68,68,0)}}`}</style>
        <motion.button initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.5 }}
          onClick={onNewInspection} whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
          style={{ position:'fixed', top:'16px', right:'16px', zIndex:50, display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'12px', border:'none', cursor:'pointer', background:'rgba(0,0,0,0.25)', backdropFilter:'blur(8px)', color:'rgba(255,255,255,0.75)', fontSize:'13px', fontWeight:700, fontFamily:'inherit' }}>
          ✕ Cerrar
        </motion.button>
        <WarningSparks />
        <motion.div initial={{ scale:0.82, opacity:0, y:50 }} animate={{ scale:1, opacity:1, y:0 }}
          transition={{ type:'spring', stiffness:110, damping:18 }}
          style={{ position:'relative', zIndex:2, maxWidth:'480px', width:'100%', textAlign:'center', background: darkMode?'rgba(18,8,4,0.88)':'rgba(255,255,255,0.88)', backdropFilter:'blur(24px)', borderRadius:'32px', padding:'40px 32px', boxShadow: darkMode?'0 24px 80px rgba(0,0,0,0.6)':'0 24px 80px rgba(239,68,68,0.14)' }}>
          <div style={{ position:'relative', display:'inline-block', marginBottom:'22px' }}>
            <motion.div initial={{ scale:0, rotate:20 }} animate={{ scale:1, rotate:0 }}
              transition={{ type:'spring', stiffness:180, damping:14, delay:0.2 }}
              style={{ width:'92px', height:'92px', borderRadius:'50%', background:'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 10px 36px rgba(239,68,68,0.4)', animation:'warn-pulse 2s ease-in-out infinite' }}>
              <AlertTriangle style={{ width:'46px', height:'46px', color:'white' }} />
            </motion.div>
          </div>
          <motion.h1 initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.4 }}
            style={{ margin:'0 0 6px', fontSize:'32px', fontWeight:900, letterSpacing:'-0.5px', background:'linear-gradient(135deg,#d97706,#dc2626)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
            ¡Chequeo Incompleto!
          </motion.h1>
          <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.52 }}
            style={{ margin:'0 0 22px', fontSize:'16px', color: darkMode?'rgba(255,255,255,0.45)':'#64748b', fontWeight:500 }}>
            ⚠️ Tenés <strong style={{ color: darkMode?'#fbbf24':'#d97706' }}>{uniquePending.length}</strong> ítem{uniquePending.length>1?'s':''} pendiente{uniquePending.length>1?'s':''}
          </motion.p>
          <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.58 }}
            style={{ display:'flex', gap:'10px', marginBottom:'20px' }}>
            <StatChip value={checkedItems.length}  label="Verificados" color="#22c55e" delay={0.62} darkMode={darkMode} />
            <StatChip value={uniquePending.length} label="Pendientes"  color="#ef4444" delay={0.70} darkMode={darkMode} />
            <StatChip value={`${pct}%`}            label="Completado"  color="#f59e0b" delay={0.78} darkMode={darkMode} />
          </motion.div>
          <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.65 }}
            style={{ background: darkMode?'rgba(245,158,11,0.05)':'rgba(254,243,199,0.6)', borderRadius:'20px', padding:'16px 18px', border: darkMode?'1px solid rgba(239,68,68,0.16)':'1px solid rgba(245,158,11,0.3)', marginBottom:'22px', maxHeight:'200px', overflowY:'auto', textAlign:'left' }}>
            {uniquePending.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity:0, x:-16 }} animate={{ opacity:1, x:0 }} transition={{ delay:0.7+idx*0.06 }}
                style={{ display:'flex', alignItems:'flex-start', gap:'10px', padding:'9px 0', borderBottom:idx<uniquePending.length-1?(darkMode?'1px solid rgba(239,68,68,0.1)':'1px solid rgba(245,158,11,0.2)'):'none' }}>
                <span style={{ width:'22px', height:'22px', borderRadius:'8px', background:'linear-gradient(135deg,#f59e0b,#ef4444)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:800, color:'white', flexShrink:0, marginTop:'1px' }}>{idx+1}</span>
                <span style={{ fontSize:'14px', fontWeight:500, color: darkMode?'rgba(255,255,255,0.78)':'#78350f', lineHeight:'1.4' }}>{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
          <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.82 }}
            style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            <motion.button onClick={onReviewSkipped} whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'17px', background:'linear-gradient(135deg,#f59e0b,#ef4444)', color:'white', border:'none', borderRadius:'18px', fontWeight:800, fontSize:'16px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow:'0 8px 28px rgba(239,68,68,0.35)', fontFamily:'inherit' }}>
              🔄 Revisar ítems pendientes
            </motion.button>
            <motion.button onClick={handleExportPDF} disabled={pdfGenerating} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'15px', background: darkMode?'rgba(59,130,246,0.1)':'rgba(59,130,246,0.07)', color: darkMode?'#93c5fd':'#2563eb', border: darkMode?'1px solid rgba(59,130,246,0.2)':'1px solid rgba(59,130,246,0.16)', borderRadius:'18px', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
              {pdfGenerating?<Loader style={{ width:'16px', height:'16px', animation:'cs-spin 1s linear infinite' }} />:<FileDown style={{ width:'16px', height:'16px' }} />}
              Exportar PDF
            </motion.button>
            <motion.button onClick={handleShareWhatsApp} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'14px', background:'linear-gradient(135deg,#25d366,#128c7e)', color:'white', border:'none', borderRadius:'18px', fontWeight:700, fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.123 1.532 5.856L0 24l6.335-1.652A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.886 0-3.646-.498-5.168-1.37l-.371-.219-3.857 1.007 1.03-3.748-.24-.386A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              Compartir por WhatsApp
            </motion.button>
            <motion.button
              onClick={() => setShowPendingConfirm(true)}
              whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'13px', background: darkMode?'rgba(255,255,255,0.03)':'rgba(15,23,42,0.03)', color: darkMode?'rgba(255,255,255,0.35)':'rgba(15,23,42,0.35)', border: darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid rgba(15,23,42,0.06)', borderRadius:'18px', fontWeight:600, fontSize:'13px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit', opacity:0.75 }}>
              <PlusCircle style={{ width:'14px', height:'14px' }} /> Nueva Inspección (con pendientes)
            </motion.button>
          </motion.div>
          <ConfirmModal
            isOpen={showPendingConfirm}
            title={tAdmin('confirm_modal_pending_inspection_title')}
            message={tAdmin('confirm_modal_pending_inspection_message')}
            confirmLabel={tAdmin('confirm_modal_pending_inspection_btn')}
            variant="warning"
            onConfirm={() => { setShowPendingConfirm(false); onNewInspection(); }}
            onCancel={() => setShowPendingConfirm(false)}
            darkMode={darkMode}
          />
        </motion.div>
      </div>
    );
  }

  // ══ SUCCESS SCREEN ═════════════════════════════════════════════
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', background:successBg, position:'relative', overflow:'hidden' }}>
      <style>{`@keyframes cs-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      <motion.button initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.5 }}
        onClick={onNewInspection} whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
        style={{ position:'fixed', top:'16px', right:'16px', zIndex:50, display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'12px', border:'none', cursor:'pointer', background:'rgba(0,0,0,0.2)', backdropFilter:'blur(8px)', color:'rgba(255,255,255,0.75)', fontSize:'13px', fontWeight:700, fontFamily:'inherit' }}>
        ✕ Cerrar
      </motion.button>
      <Confetti />

      <motion.div initial={{ scale:0.82, opacity:0, y:50 }} animate={{ scale:1, opacity:1, y:0 }}
        transition={{ type:'spring', stiffness:110, damping:18 }}
        style={{ position:'relative', zIndex:2, maxWidth:'460px', width:'100%', textAlign:'center', background: darkMode?'rgba(8,18,12,0.88)':'rgba(255,255,255,0.88)', backdropFilter:'blur(24px)', borderRadius:'32px', padding:'clamp(24px, 5vw, 40px) clamp(20px, 5vw, 32px)', boxShadow: darkMode?'0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(34,197,94,0.12)':'0 24px 80px rgba(34,197,94,0.18), 0 0 0 1px rgba(34,197,94,0.12)' }}>

        <div style={{ position:'relative', display:'inline-block', marginBottom:'22px' }}>
          {[0,1,2].map(i => (
            <motion.div key={i} initial={{ scale:0.6, opacity:0.5 }} animate={{ scale:1.9+i*0.5, opacity:0 }}
              transition={{ duration:2, delay:0.4+i*0.35, repeat:Infinity, repeatDelay:0.8 }}
              style={{ position:'absolute', inset:0, borderRadius:'50%', border:`1.5px solid ${darkMode?'rgba(74,222,128,0.35)':'rgba(34,197,94,0.45)'}`, pointerEvents:'none' }} />
          ))}
          <motion.div initial={{ scale:0, rotate:-45 }} animate={{ scale:1, rotate:0 }}
            transition={{ type:'spring', stiffness:180, damping:14, delay:0.25 }}
            style={{ width:'92px', height:'92px', borderRadius:'50%', background:'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 10px 36px rgba(34,197,94,0.45)' }}>
            <CheckCircle2 style={{ width:'46px', height:'46px', color:'white' }} />
          </motion.div>
        </div>

        <motion.h1 initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.45 }}
          style={{ margin:'0 0 6px', fontSize:'34px', fontWeight:900, letterSpacing:'-1px', background:'linear-gradient(135deg,#16a34a,#15803d)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text' }}>
          {authFlow === 'authorized' || authFlow === 'override' ? 'Vuelo Autorizado' : (PHASE_APPROVED_TITLES[activePhase] || 'Prevuelo Aprobado')}
        </motion.h1>

        {savedInspectionId && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)', border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(15,23,42,0.1)', marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: darkMode ? 'rgba(255,255,255,0.35)' : '#94a3b8', letterSpacing: '0.1em', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
              REG. #{savedInspectionId.slice(0,8).toUpperCase()} · {new Date().toLocaleDateString('es-AR')} {new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} UTC
            </span>
          </motion.div>
        )}

        <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.58 }}
          style={{ margin:'0 0 26px', fontSize:'15px', color: darkMode?'rgba(255,255,255,0.45)':'#64748b', fontWeight:500 }}>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <span style={{ fontSize:16, fontWeight:700, color: darkMode?'rgba(255,255,255,0.8)':'#374151', display:'flex', alignItems:'center', gap:6 }}>
              <motion.span animate={{ x:[0,6,0], rotate:[0,8,0] }} transition={{ duration:2, repeat:Infinity, ease:'easeInOut' }} style={{ display:'inline-block' }}>✈️</motion.span>
              {sessionData?.aircraft ? `${sessionData.aircraft.manufacturer} ${sessionData.aircraft.model_name}` : 'Aeronave'}
            </span>
            <span style={{ fontSize:13, color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>
              {sessionData?.aircraft?.registration || ''} · {sessionData?.date ? new Date(sessionData.date + 'T00:00:00').toLocaleDateString('es-AR') : new Date().toLocaleDateString('es-AR')}
            </span>
            {pilotName && (
              <span style={{ fontSize:13, color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8' }}>
                👨‍✈️ {pilotName}
              </span>
            )}
          </div>
        </motion.p>

        <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.62 }}
          style={{ display:'flex', gap:'10px', marginBottom:'20px' }}>
          <StatChip value={checkedItems.length} label="Ítems OK"      color="#22c55e" delay={0.68} darkMode={darkMode} />
          <StatChip value={`${pct}%`}           label="Cumplimiento"  color="#3b82f6" delay={0.78} darkMode={darkMode} />
          <StatChip value={totalItems}          label="Total chequeo" color="#8b5cf6" delay={0.88} darkMode={darkMode} />
        </motion.div>

        <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.72 }}
          style={{ borderRadius:'20px', background: darkMode?'rgba(34,197,94,0.06)':'rgba(34,197,94,0.06)', border: darkMode?'1px solid rgba(34,197,94,0.16)':'1px solid rgba(34,197,94,0.18)', padding:'16px 20px', marginBottom: observationItems.length > 0 ? '12px' : '24px' }}>
          <p style={{ margin:'0 0 4px', fontWeight:700, fontSize:'14px', color: darkMode?'#4ade80':'#15803d', display:'flex', alignItems:'center', justifyContent:'center', gap:'7px' }}>
            <CheckCircle2 style={{ width:'16px', height:'16px' }} /> Todos los ítems han sido verificados
          </p>
          {isLastPhase && (
            <p style={{ margin:'0 0 12px', fontSize:'13px', color: darkMode?'rgba(74,222,128,0.6)':'#16a34a' }}>
              El avión está listo para operar de forma segura
            </p>
          )}
          <AnimatePresence>
            {(saveSuccess || localSaveComplete) && (
              <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'6px', padding:'7px 12px', borderRadius:'20px', background: darkMode?'rgba(34,197,94,0.09)':'rgba(34,197,94,0.07)', border: darkMode?'1px solid rgba(34,197,94,0.2)':'1px solid rgba(34,197,94,0.18)' }}>
                <span style={{ fontSize:'12px' }}>✅</span>
                <span style={{ fontSize:'12px', fontWeight:700, color: darkMode?'#4ade80':'#16a34a' }}>
                  Registro guardado · {new Date().toLocaleTimeString('es-AR', { hour:'2-digit', minute:'2-digit' })} hs
                </span>
              </motion.div>
            )}
            {savedOffline && (
              <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0 }}
                style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', padding:'10px 14px', borderRadius:'12px', background: darkMode?'rgba(245,158,11,0.09)':'rgba(245,158,11,0.07)', border: darkMode?'1px solid rgba(245,158,11,0.18)':'1px solid rgba(245,158,11,0.14)' }}>
                <span style={{ fontSize:'14px' }}>📡</span>
                <span style={{ fontSize:'13px', fontWeight:600, color: darkMode?'#fcd34d':'#92400e' }}>Guardado localmente — Background Sync activo</span>
              </motion.div>
            )}
            {authData?.instructor_note && (
              <motion.div initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }}
                style={{ marginTop:'8px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', padding:'10px 14px', borderRadius:'12px', background: darkMode?'rgba(22,163,74,0.09)':'rgba(22,163,74,0.07)', border: darkMode?'1px solid rgba(22,163,74,0.18)':'1px solid rgba(22,163,74,0.14)' }}>
                <Shield style={{ width:'14px', height:'14px', color:'#16a34a' }} />
                <span style={{ fontSize:'13px', fontWeight:600, color:'#16a34a' }}>Instructor: "{authData.instructor_note}"</span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {observationItems.length > 0 && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.85 }}
            style={{ marginBottom:'20px', padding:'10px 14px', borderRadius:'14px', background: darkMode?'rgba(234,179,8,0.08)':'rgba(254,243,199,0.7)', border: darkMode?'1px solid rgba(234,179,8,0.2)':'1px solid rgba(234,179,8,0.35)', display:'flex', alignItems:'center', gap:'8px' }}>
            <span style={{ fontSize:'16px' }}>⚠️</span>
            <span style={{ fontSize:'13px', fontWeight:600, color: darkMode?'#fcd34d':'#92400e' }}>
              {observationItems.length} observación{observationItems.length>1?'es':''} registrada{observationItems.length>1?'s':''} — incluida{observationItems.length>1?'s':''} en el PDF
            </span>
          </motion.div>
        )}

        <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.88 }}
          style={{ display:'flex', flexDirection:'column', gap:'10px' }}>

          {orgFeatures?.digital_signature !== false && (!signatureData ? (
            <div style={{ borderRadius:'16px', padding:'14px', background: darkMode?'rgba(37,99,235,0.07)':'rgba(219,234,254,0.4)', border: darkMode?'1px solid rgba(37,99,235,0.15)':'1px solid rgba(37,99,235,0.2)' }}>
              <p style={{ margin:'0 0 10px', fontSize:'12px', fontWeight:700, color: darkMode?'#93c5fd':'#1d4ed8', textAlign:'center' }}>
                Firma digital del piloto
              </p>
              <SignaturePad
                darkMode={darkMode}
                onSave={(data) => setSignatureData(data)}
                onClear={() => setSignatureData(null)}
              />
            </div>
          ) : (
            <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
              style={{ borderRadius:'14px', padding:'10px 14px', background: darkMode?'rgba(34,197,94,0.08)':'rgba(220,252,231,0.6)', border: darkMode?'1px solid rgba(34,197,94,0.2)':'1px solid rgba(34,197,94,0.3)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ fontSize:'16px' }}>✅</span>
                <span style={{ fontSize:'13px', fontWeight:700, color: darkMode?'#4ade80':'#16a34a' }}>Firma registrada</span>
              </div>
              <button onClick={() => setSignatureData(null)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:'11px', color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8', fontFamily:'inherit' }}>
                Cambiar
              </button>
            </motion.div>
          ))}

          {/* Sub-etapa 3.2c-iii — el PDF es consolidado (las 3 fases juntas):
              solo se genera al cerrar el chequeo completo. En fases
              intermedias no tiene sentido exportarlo todavía (faltarían
              secciones), así que el botón se reemplaza por un texto. Madryn
              (1 fase) tiene isLastPhase siempre true → sin cambios. */}
          {isLastPhase ? (
            <motion.button onClick={handleExportPDF} disabled={pdfGenerating} whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'17px', background:'linear-gradient(135deg,#2563eb,#6d28d9)', color:'white', border:'none', borderRadius:'18px', fontWeight:800, fontSize:'16px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow:'0 8px 28px rgba(37,99,235,0.38)', fontFamily:'inherit' }}>
              {pdfGenerating ? <><Loader style={{ width:'18px', height:'18px', animation:'cs-spin 1s linear infinite' }} /> Generando PDF...</> : <><FileDown style={{ width:'18px', height:'18px' }} /> {signatureData ? 'Exportar PDF con firma' : 'Exportar PDF'}</>}
            </motion.button>
          ) : (
            <p style={{ margin:'0', fontSize:'12px', textAlign:'center', color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8', fontFamily:'inherit' }}>
              El PDF se genera al finalizar el chequeo
            </p>
          )}
          <motion.button onClick={handleShareWhatsApp} whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
            style={{ width:'100%', padding:'15px', background:'linear-gradient(135deg,#25d366,#128c7e)', color:'white', border:'none', borderRadius:'18px', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow:'0 6px 20px rgba(37,211,102,0.3)', fontFamily:'inherit' }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.123 1.532 5.856L0 24l6.335-1.652A11.955 11.955 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.886 0-3.646-.498-5.168-1.37l-.371-.219-3.857 1.007 1.03-3.748-.24-.386A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
            Compartir por WhatsApp
          </motion.button>
          {isLastPhase ? (
            <motion.button onClick={onNewInspection} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
              style={{ width:'100%', padding:'15px', background: darkMode?'rgba(255,255,255,0.05)':'rgba(15,23,42,0.04)', color: darkMode?'rgba(255,255,255,0.65)':'rgba(15,23,42,0.6)', border: darkMode?'1px solid rgba(255,255,255,0.09)':'1px solid rgba(15,23,42,0.09)', borderRadius:'18px', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
              <PlusCircle style={{ width:'17px', height:'17px' }} /> Nueva Inspección
            </motion.button>
          ) : (
            <>
              {/* Botón sin i18n (como el resto de esta franja de acciones) —
                  se fuerza es-AR para el nombre de fase y así no mezclar
                  idiomas con "Continuar con"/"Nueva Inspección" (Sub-etapa
                  3.2c-i, ver reporte). */}
              <motion.button onClick={() => onContinuePhase?.(nextPhase, signatureData)} whileHover={{ scale:1.02, y:-2 }} whileTap={{ scale:0.97 }}
                style={{ width:'100%', padding:'17px', background:'linear-gradient(135deg,#2563eb,#6d28d9)', color:'white', border:'none', borderRadius:'18px', fontWeight:800, fontSize:'16px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', boxShadow:'0 8px 28px rgba(37,99,235,0.38)', fontFamily:'inherit' }}>
                <Plane style={{ width:'18px', height:'18px' }} /> Continuar con {nextPhase ? t(`phase_${nextPhase}`, { lng: 'es-AR' }) : 'la próxima fase'}
              </motion.button>
              {/* Sub-etapa 3.2c-ii — nextPhase opcional (no está en
                  required_phases de la org): se ofrece la alternativa de
                  saltearla. Nunca aparece para una fase obligatoria ni para
                  Madryn (ahí nextPhase siempre es null). */}
              {isNextPhaseOptional && (
                <>
                  <p style={{ margin:'-4px 0 0', fontSize:'12px', textAlign:'center', color: darkMode?'rgba(255,255,255,0.4)':'#94a3b8', fontFamily:'inherit' }}>
                    Esta fase es opcional para tu aeroclub
                  </p>
                  <motion.button onClick={() => onSkipPhase?.(nextPhase)} whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                    style={{ width:'100%', padding:'15px', background: darkMode?'rgba(255,255,255,0.05)':'rgba(15,23,42,0.04)', color: darkMode?'rgba(255,255,255,0.65)':'rgba(15,23,42,0.6)', border: darkMode?'1px solid rgba(255,255,255,0.09)':'1px solid rgba(15,23,42,0.09)', borderRadius:'18px', fontWeight:700, fontSize:'15px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', fontFamily:'inherit' }}>
                    <SkipForward style={{ width:'17px', height:'17px' }} /> Saltear {t(`phase_${nextPhase}`, { lng: 'es-AR' })}
                  </motion.button>
                </>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CompletionScreen;
